import React, {useEffect, useState, useRef} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {PricingBreakdownList} from "../Components/pricingBreakdownList.jsx";
import {PayAfterDropoffContent} from "./payAfterDropoff.jsx";
import {InterestFreePaymentContent} from "./interestFreePayment.jsx";
import {FlightDelayedContent} from "../Components/flightDelayedContent.jsx";
import {CancellationModificationRefundContent} from "../Components/cancellationModificationRefund.jsx";
import {NoSurprisePopupContent} from "../Components/noSurprisePopupContent.jsx";
import {SpacialRequestForm} from "../Components/spacialRequestForm.jsx";
import {AirportPickupFeeInfo} from "../Components/airportPickupFeeInfo.jsx";
import {CouponForm, CouponFormInput, CouponFormSubmit} from "../Components/couponForm.jsx";
import {HopnReviewsWidget} from "../Components/hopnReviewsWidget.jsx";
import {PrebookCompareDetail} from "../Components/prebookCompare.jsx";
import WhyChooseUs from "@/customComponents/sharedComponents/WhyChooseUs.jsx";
import PopularQuestions, {FaqAccordionList} from "@/customComponents/sharedComponents/PopularQuestions.jsx";
import ExclusivePerksBar from "@/customComponents/sharedComponents/ExclusivePerksBar.jsx";
import {getFaqsForFlow, getTripDirection, FAQ_INLINE_COUNT} from "@/constants/faqs.js";
import {isNearAirport} from "@/utils/airports";
import {filterSelectedCarDetails} from "@/utils/filesConstants.js";
import {PaymentCardsContent} from "./paymentCards.jsx";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {getToken} from "@/utils/authToken";
import {usePaymentMethods} from "@/hooks/usePaymentMethods";
import {tripService} from "@/services/tripService";
import {img} from "@/utils/pluginImg.js";
import {POLICY_CONSTANTS} from "@/constants/booking";
import {comparePrices, netTotalCents} from "@/utils/pricing.js";

// "Why Choose us?" perks that open a nested content view inside the compare modal.
// Each reuses the same content component as its standalone step (no "coming soon"
// needed — all three exist). Keyed by the compareView value the perk sets.
const WHY_PERK_VIEWS = {
    afterpay: {title: "Ride now Pay later", Content: InterestFreePaymentContent},
    flight: {title: "Flight tracking", Content: FlightDelayedContent},
    payafter: {title: "", Content: PayAfterDropoffContent},
    // No header title — the CMR content carries its own tabbed header.
    cancellation: {title: "", Content: CancellationModificationRefundContent},
};

// Short location label for the compare row: airport code › city › first address chunk.
const shortLabel = (dest) => {
    if (!dest) return '';
    // Prefer the airport IATA code when the point is AT/near an airport — its address
    // often reads "YYZ Terminal 1, Mississauga, ON…", so the city fallback below would
    // wrongly show "Mississauga". Use the canonical detector (same one that decides the
    // trip direction) so the label matches the flow.
    const near = dest.lat && dest.lng ? isNearAirport(dest.lat, dest.lng) : false;
    if (near && near.code) return near.code;
    const code = dest.address?.match(/\(([A-Z]{3})\)/);
    if (code) return code[1];
    if (dest.city) return dest.city;
    return (dest.address || '').split(',')[0].trim();
};

// Main price shows the pre-tax net_total from the backend breakdown, and the
// ride-share estimate is that same total without the online/coupon discounts —
// both live in utils/pricing.js (shared with the mobile step + booking received).

export function SelectedCarDetailsLatest(props) {
    const [recalculateError, setRecalculateError] = useState(false);
    const [allCarsFromDb, setAllCarsFromDb] = useState(storage.get(STORAGE_KEYS.ALL_CARS_API, []));

    // Popups (parity with carSelection.jsx)
    const [paymentDetailsPopup, setPaymentDetailsPopup] = useState(false);
    const [airportFeeInfo, setAirportFeeInfo] = useState(false);
    const [squareModalPopup, setSquareModalPopup] = useState(false);
    const [flatRatePopup, setFlatRatePopup] = useState(false);
    const [spacialRequestPopup, setSpacialRequestPopup] = useState(false);
    const [applyCoupon, setApplyCoupon] = useState(false);
    // "Credit/Debit Card" footer row — opens the shared Secure Payment content
    // (PaymentCardsContent): desktop = nested .upper-modal popup, mobile = the
    // parallel `personalCard` step.
    const [personalCardPopup, setPersonalCardPopup] = useState(false);
    const openPersonalCard = () => window.innerWidth >= 576
        ? setPersonalCardPopup(true)
        : props.setStep('personalCard');
    // The card row only shows to logged-in users (no saved card without an account).
    const isLoggedIn = !!getToken();
    // Saved cards, so the row can name the one that will actually be charged.
    const {data: savedCards = []} = usePaymentMethods({enabled: isLoggedIn});
    // Which card the row names. Held in STATE rather than read straight from storage on
    // every render, because sessionStorage isn't reactive: the panel writes USER_CARD_ID
    // as soon as the user picks a card, and closing it re-reads that here so the row
    // follows the choice. (Mobile needs no equivalent — the `personalCard` step unmounts
    // this component, so coming back re-initialises from storage.)
    // Read from storage ON EVERY RENDER, not seeded once into state. personalCard is a
    // POPUP_STEPS step, so on mobile it opens as a STACKED overlay and this component
    // stays mounted underneath — a one-time seed never re-ran, and the row kept naming
    // the previous card until some other step change happened to remount it. This
    // component re-renders when the popup closes (desktop) and when the overlay stack
    // changes (mobile), so a plain read is always current.
    const chosenCardId = storage.get(STORAGE_KEYS.USER_CARD_ID);
    const closePersonalCard = () => setPersonalCardPopup(false);
    // The chosen card, else the first saved one — the same fallback paymentCards uses to
    // pre-select, so the row and the "Available Cards" list can't disagree.
    const defaultCardLabel = (() => {
        if (!savedCards.length) return '';
        const card = savedCards.find((c) => String(c.id) === String(chosenCardId)) || savedCards[0];
        let metadata = {};
        try { metadata = JSON.parse(card.metadata); } catch {}
        return `${metadata?.card?.brand || 'Card'} ****${metadata?.card?.last4 || '****'}`;
    })();
    // Cancellation / Modification / Refund tabbed popup (desktop) — mobile routes to
    // the parallel `cancellationModificationRefund` step.
    const [cancellationPopup, setCancellationPopup] = useState(false);
    const openCancellation = () => window.innerWidth >= 576
        ? setCancellationPopup(true)
        : props.setStep('cancellationModificationRefund');
    // "Pay after drop off" is a modal now, not an inline accordion — the full
    // Secure-Payment content doesn't fit in a one-line perk row. Same dual mode:
    // desktop popup, mobile step.
    const openPayAfter = () => window.innerWidth >= 576
        ? setSquareModalPopup(true)
        : props.setStep('payAfterDropoff');

    // Inline accordions
    const [openPerk, setOpenPerk] = useState(null);     // 'pay' | 'cancel' | 'flat'
    // "More" FAQ nested modal (the rest of POPULAR_QUESTIONS)
    const [faqModal, setFaqModal] = useState(false);
    // Flow-specific FAQs (from docs); policy-dependent answers use the live policy text.
    const faqs = getFaqsForFlow(getTripDirection(), {
        freeCancellationAvailable: POLICY_CONSTANTS.FREE_CANCELLATION_AVAILABLE,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        freeEditsCount: POLICY_CONSTANTS.FREE_EDITS_COUNT,
        editableHoursBeforePickup: POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP,
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
    });
    // "Cancellation & Changes" perk copy: each half falls back to an "according
    // to the policies" line when its hours value is 0/missing (0 means the
    // policies apply, not that the action is unavailable).
    const cancelHrs = POLICY_CONSTANTS.FREE_CANCELLATION_HOURS;
    const changeHrs = POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP;
    const cancellationChangesText = cancelHrs > 0 && changeHrs > 0
        ? `Free cancellation up to ${cancelHrs} hours and changes up to ${changeHrs} hours before your scheduled pickup time.`
        : [
            cancelHrs > 0
                ? `Free cancellation up to ${cancelHrs} hours before your scheduled pickup time.`
                : 'You can cancel the booking according to the policies.',
            changeHrs > 0
                ? `Free changes up to ${changeHrs} hours before your scheduled pickup time.`
                : 'You can update the booking according to the policies.',
        ].join(' ');
    // Prebook-vs-Ride-Sharing nested modal: open state + which view ('compare' | 'more')
    const [compareModal, setCompareModal] = useState(false);
    const [compareView, setCompareView] = useState('compare');

    const [isCarSpecialRequest, setIsCarSpecialRequest] = useState(
        storage.get(STORAGE_KEYS.CAR_SPECIAL_REQUEST) === true
    );
    const priorRequestRef = useRef(null);
    // Reset the compare modal's scroll to the top whenever the view switches
    // (e.g. "Exclusive Airport Pickup Perks" → Why Choose Us should start at the top).
    const compareContentRef = useRef(null);
    useEffect(() => {
        if (compareModal) compareContentRef.current?.scrollTo(0, 0);
    }, [compareView, compareModal]);

    // Cars: use cache if present, else compute once (mirrors carSelection.jsx:51-78).
    useEffect(() => {
        if (storage.has(STORAGE_KEYS.ALL_CARS_API)) {
            const cachedCars = storage.get(STORAGE_KEYS.ALL_CARS_API, []);
            if (cachedCars.length > 0 && !storage.get(STORAGE_KEYS.SELECTED_CAR)) {
                const defaultCar = cachedCars.find(c => c.is_recommended) || cachedCars[0];
                storage.set(STORAGE_KEYS.SELECTED_CAR, defaultCar.vehicle_type_id);
            }
            return;
        }
        const hopnInitCars = async () => {
            const allCars = await tripService.recalculateCost();
            if (allCars.success && allCars.data) {
                setAllCarsFromDb(allCars.data);
                if (allCars.data.length > 0) {
                    const defaultCar = allCars.data.find(c => c.is_recommended) || allCars.data[0];
                    storage.set(STORAGE_KEYS.SELECTED_CAR, defaultCar.vehicle_type_id);
                }
            } else {
                setRecalculateError(allCars.error);
            }
        };
        hopnInitCars();
    }, []);

    const carDetails = filterSelectedCarDetails() || {};
    const isLoading = !allCarsFromDb.length && !carDetails.vehicle_type_id;
    const pricing = carDetails.pricing || {};
    const price = Math.ceil(netTotalCents(carDetails) / 100);
    const oldPrice = pricing.total_before_discounts
        ? Math.ceil(pricing.total_before_discounts / 100)
        : null;
    const discountPercent = pricing.online_discount_percentage || 0;
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;
    const hasCoupon = !!storage.get(STORAGE_KEYS.COUPON_VALUE);

    // Compare row (Prebook vs Ride-Sharing) — driven by the actual route + price.
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const pickupLabel = shortLabel(destinations[0]);
    const dropoffLabel = shortLabel(destinations[1]);
    const {sharePrice} = comparePrices(carDetails);

    const handleSpecialRequestChange = () => {
        const newValue = !isCarSpecialRequest;
        setIsCarSpecialRequest(newValue);
        storage.set(STORAGE_KEYS.CAR_SPECIAL_REQUEST, newValue);
        if (newValue) {
            priorRequestRef.current = {
                text: storage.get(STORAGE_KEYS.SPECIAL_REQUEST, ""),
                pet: storage.get(STORAGE_KEYS.CARING_PET, false),
                quiet: storage.get(STORAGE_KEYS.QUIET_RIDE, false),
            };
        }
        setSpacialRequestPopup(newValue);
    };

    const handleSpecialRequestClick = () => {
        if (window.innerWidth >= 576) {
            handleSpecialRequestChange();
            return;
        }
        if (!isCarSpecialRequest) {
            props.setStep('spacialRequest');
        } else {
            setIsCarSpecialRequest(false);
            storage.set(STORAGE_KEYS.CAR_SPECIAL_REQUEST, false);
            storage.remove(STORAGE_KEYS.SPECIAL_REQUEST);
            storage.set(STORAGE_KEYS.CARING_PET, false);
            storage.set(STORAGE_KEYS.QUIET_RIDE, false);
        }
    };

    const handleContinue = () => {
        props.setStep('orderDetails');
    };

    return (
        <>
            {/* Personal Card — shared Secure Payment content in a nested popup (desktop).
                Conditionally mounted so Square only initialises while the popup is open. */}
            {personalCardPopup && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true} title="" extraClasses="less-space" totalSteps={0}
                            cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                            setStep={props.setStep} closeModal={closePersonalCard}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <PaymentCardsContent setStep={props.setStep}/>
                        </div>
                    </div>
                </div>
            )}

            {/* Apply Coupon Popup (desktop) */}
            {applyCoupon && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true}
                            title="Add A Coupon"
                            extraClasses=""
                            totalSteps={0}
                            cancelButton={true}
                            cancelButtonType="icon"
                            backStep={null}
                            showBackButton={false}
                            setStep={props.setStep}
                            closeModal={() => setApplyCoupon(false)}
                        />
                        <CouponForm
                            carDetails={filterSelectedCarDetails()}
                            setApplyCoupon={(val) => {
                                setApplyCoupon(val);
                                if (!val) setAllCarsFromDb(storage.get(STORAGE_KEYS.ALL_CARS_API, []));
                            }}
                        >
                            <div className="upper-modal-content">
                                <div className="hopn-spacial-request modalPages-spacing">
                                    <CouponFormInput/>
                                </div>
                            </div>
                            <div className="hopn-modal-footer">
                                <CouponFormSubmit/>
                            </div>
                        </CouponForm>
                    </div>
                </div>
            )}

            {/* Pay after drop-off popup */}
            <div className="upper-modal desktop" style={{display: squareModalPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="" extraClasses="" totalSteps={0}
                        cancelButton={true} cancelButtonType="text" backStep={null} showBackButton={false}
                        setStep={props.setStep} closeModal={() => setSquareModalPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing"><PayAfterDropoffContent/></div>
                    </div>
                </div>
            </div>

            {/* Guaranteed flat rate popup */}
            <div className="upper-modal" style={{display: flatRatePopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="No Surprises" extraClasses="" totalSteps={0}
                        cancelButton={true} cancelButtonType="text" backStep={null} showBackButton={false}
                        setStep={props.setStep} closeModal={() => setFlatRatePopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing"><NoSurprisePopupContent/></div>
                    </div>
                </div>
            </div>

            {/* Special Request popup */}
            {/* Cancellation / Modification / Refund tabbed popup (desktop) */}
            <div className="upper-modal" style={{display: cancellationPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="" buttonStyle="elegant"
                        extraClasses="" totalSteps={0}
                        cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                        setStep={props.setStep} closeModal={() => setCancellationPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing"><CancellationModificationRefundContent/></div>
                    </div>
                </div>
            </div>

            <div className="upper-modal spacial-request" style={{display: spacialRequestPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="Spacial Request" extraClasses="spacial-request" totalSteps={0}
                        cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                        setStep={props.setStep}
                        closeModal={() => {
                            setSpacialRequestPopup(false);
                            const prior = priorRequestRef.current;
                            storage.set(STORAGE_KEYS.CARING_PET, prior?.pet ?? false);
                            storage.set(STORAGE_KEYS.QUIET_RIDE, prior?.quiet ?? false);
                            if (prior?.text?.trim()) {
                                storage.set(STORAGE_KEYS.SPECIAL_REQUEST, prior.text);
                            } else {
                                storage.remove(STORAGE_KEYS.SPECIAL_REQUEST);
                                setIsCarSpecialRequest(false);
                                storage.set(STORAGE_KEYS.CAR_SPECIAL_REQUEST, false);
                            }
                        }}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing">
                            <SpacialRequestForm setSpacialRequestPopup={setSpacialRequestPopup}
                                                setIsCarSpecialRequest={setIsCarSpecialRequest}/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Breakdown popup */}
            {paymentDetailsPopup && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        {airportFeeInfo ? (
                            <>
                                <ModalHeader
                                    inline={true} title="Airport Pickup Fee" extraClasses="less-space" totalSteps={0}
                                    cancelButton={false} backStep="back" showBackButton={true}
                                    setStep={() => setAirportFeeInfo(false)}
                                    closeModal={() => setPaymentDetailsPopup(false)}
                                />
                                <div className="upper-modal-content padding-bottom-30">
                                    <div className="modalPages-spacing"><AirportPickupFeeInfo/></div>
                                </div>
                            </>
                        ) : (
                            <>
                                <ModalHeader
                                    inline={true} title="" extraClasses="less-space" totalSteps={0}
                                    cancelButton={true} cancelButtonType="text" backStep={null} showBackButton={false}
                                    setStep={props.setStep} closeModal={() => setPaymentDetailsPopup(false)}
                                />
                                <div className="upper-modal-content padding-bottom-30">
                                    <h2 className="subtitle modalPages-spacing">Payment</h2>
                                    <div className="modalPages-spacing">
                                        <PricingBreakdownList props={props} hideKeys={['tax', 'grand_total']} stripExclTax={true}
                                                              onAirportFeeClick={() => setAirportFeeInfo(true)}/>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* "More" popular questions nested modal (the rest of POPULAR_QUESTIONS) */}
            {faqModal && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true}
                            title="Popular questions"
                            extraClasses="less-space"
                            totalSteps={0}
                            cancelButton={true}
                            cancelButtonType="icon"
                            backStep={null}
                            showBackButton={false}
                            setStep={props.setStep}
                            closeModal={() => setFaqModal(false)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="hopn-ride-latest">
                                <div className="modalPages-spacing">
                                    <div className="hopn-ride-faq">
                                        <FaqAccordionList items={faqs.slice(FAQ_INLINE_COUNT)} findingDriverPopup/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Prebook vs Ride-Sharing nested modal (compare → "More" → why-choose-us → perk detail) */}
            {compareModal && (() => {
                const perkView = WHY_PERK_VIEWS[compareView];   // afterpay | flight | payafter
                const PerkContent = perkView?.Content;
                return (
                    <div className="upper-modal" style={{display: 'flex'}}>
                        <div className="upper-modal-inner">
                            <ModalHeader
                                inline={true}
                                title={perkView?.title || ""}
                                extraClasses="less-space"
                                totalSteps={0}
                                cancelButton={true}
                                cancelButtonType="icon"
                                backStep={compareView === 'compare' ? null : "back"}
                                showBackButton={compareView !== 'compare'}
                                // perk detail → back to "more"; "more" → back to "compare"
                                setStep={() => setCompareView(perkView ? 'more' : 'compare')}
                                closeModal={() => setCompareModal(false)}
                            />
                            <div
                                ref={compareContentRef}
                                className={`upper-modal-content ${compareView !== 'compare' ? 'padding-bottom-30' : ''}`}
                            >
                                <div className="modalPages-spacing">
                                    {perkView
                                        ? <PerkContent/>
                                        : compareView === 'more'
                                            ? <WhyChooseUs onPerkAction={setCompareView}/>
                                            : <PrebookCompareDetail
                                                pickup={pickupLabel}
                                                dropoff={dropoffLabel}
                                                price={price}
                                                sharePrice={sharePrice}/>}
                                </div>
                            </div>
                            {compareView === 'compare' && (
                                <div className="hopn-modal-footer prebook-compare">
                                    <ExclusivePerksBar onClick={() => setCompareView('more')}/>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            <ModalHeader
                title="Choose your ride"
                // The plugin's 10px gap under the header: its header has no bottom padding,
                // so less-space alone is 10px there — here it also needs pb-0 (the shared
                // header adds 30px of bottom padding on top of the margin).
                extraClasses="less-space pb-0"
                totalSteps={0}
                cancelButton={false}
                // Was "carSelection"; with that step disabled the previous screen in
                // the flow is the date/time step.
                backStep="travelingDateTime"
                showBackButton={true}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className="hopn-ride-latest">
                {/* Title — in the header on desktop. Phones hide header titles (the 767
                    block), so it shows here instead (.little-title is mobile-only). */}
                <div className="hopn-ride-latest-head little-title modalPages-spacing">
                    <h2>Choose your ride</h2>
                </div>

                {isLoading ? (
                    <div className="hopn-ride-latest-loading"><Loader/></div>
                ) : (
                    <>
                        {/* Ride card */}
                        <div className="modalPages-spacing">
                            <div className="hopn-ride-card">
                                <div className="hopn-ride-card-top">
                                    <div className="hopn-ride-card-info">
                                        <h3>
                                            {carDetails.vehicle_name}
                                            {carDetails.vehicles_needed > 1 &&
                                                ` X${carDetails.vehicles_needed}`}
                                            {carDetails.vehicle_reference &&
                                                <span> {carDetails.vehicle_reference}</span>}
                                        </h3>
                                        <p>{carDetails.vehicle_details}</p>
                                        <img className="hopn-ride-card-car" src={carDetails.vehicle_image}
                                             alt={carDetails.vehicle_name}/>
                                        <p className="hopn-ride-card-verified">
                                            <span className="icon"><img src={img("shield-protected.svg")} alt=""/></span>
                                            Verified &amp; insured drivers with limousine-level training
                                        </p>
                                    </div>

                                    <div className="hopn-ride-price-box">
                                        <div className="hopn-ride-price-box-inner">
                                            <span className="hopn-ride-tip-badge">Tip included <img src={img("circle-check-mark.svg")} alt=""/></span>
                                            <div className="hopn-ride-price-main">
                                                <span className="now">${price}</span>
                                                {oldPrice && discountPercent > 0 && (
                                                    <div className="hopn-ride-price-main-inner">
                                                        <span className="was">
                                                    <s>${oldPrice}</s>
                                                    <button
                                                        type="button"
                                                        className="hopn-ride-price-info"
                                                        onClick={() => window.innerWidth >= 576 ? setPaymentDetailsPopup(true) : props.setStep('paymentBreakDown')}
                                                        aria-label="Price breakdown"
                                                    >
                                                        <img src={img("info-icon-big.svg")} alt="Info"/>
                                                    </button>
                                                </span>
                                                        {discountPercent > 0 && (
                                                            <span
                                                                className="hopn-ride-save">Save {discountPercent}% Off</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="hopn-ride-perks">
                                            <PerkRow
                                                icon="square-big-fav.svg"
                                                title="Pay after drop off"
                                                subtitle="Square hold funds"
                                                onOpen={openPayAfter}
                                            />
                                            <PerkRow
                                                icon="cancellation-changes.svg"
                                                title="Cancellation & Changes"
                                                subtitle={cancelHrs > 0 ? `Up ${cancelHrs} hrs before pickup` : 'According to the policies'}
                                                onOpen={openCancellation}
                                            />
                                            <PerkRow
                                                icon="lock.svg"
                                                title="Guaranteed flat rate"
                                                subtitle="No surge, km, or minute charges"
                                                open={openPerk === 'flat'}
                                                onToggle={() => setOpenPerk(openPerk === 'flat' ? null : 'flat')}
                                            >
                                                The price you see is the price you pay — no surge pricing, no per-km or
                                                per-minute fees.
                                            </PerkRow>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Popular questions */}
                        <div className="modalPages-spacing">
                            <PopularQuestions
                                faqs={faqs}
                                findingDriverPopup
                                onMore={() => window.innerWidth >= 576 ? setFaqModal(true) : props.setStep('popularQuestions')}/>
                        </div>

                        {/* Google reviews — same Elfsight widget as the passenger portal */}
                        <div className="modalPages-spacing">
                            <HopnReviewsWidget layout="collapsible"/>
                        </div>

                        {/* Prebook vs Ride-sharing */}
                        <div className="modalPages-spacing">
                            <div className="hopn-ride-compare">
                                <div className="hopn-ride-compare-row">
                                    <div className="hopn-ride-compare-head">
                                        <small>{pickupLabel} <img src={img("arrow-right.svg")} alt=""/> {dropoffLabel}</small>
                                    </div>
                                    <div className="hopn-ride-compare-row-section">
                                        <div className="hopn-ride-compare-side hopn">
                                            <img src={img("compare-hopn-car.svg")} alt="" width="150" />
                                            <div className="hopn-ride-compare-content">
                                                <img src={img("logo-latest.svg")} alt="" width="64"/>
                                                <span className="amt">${price}</span>
                                                <small className="muted">BLACK · SUV · EXECUTIVE</small>
                                            </div>
                                        </div>
                                        <span className="hopn-ride-compare-vs">VS</span>
                                        <div className="hopn-ride-compare-side share">
                                            <img src={img("compare-share-car.svg")} alt="Ride-share" width="128"/>
                                            <div className="hopn-ride-compare-content">
                                                <small>Ride-share (est.)</small>
                                                <span className="amt">~${sharePrice}</span>
                                                <small className="muted">May Vary With Surge</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="full-view-button">
                                    <button
                                        type="button"
                                        className="hopn-ride-compare-toggle"
                                        onClick={() => {
                                            // Desktop: inline .upper-modal popup. Mobile: dedicated step.
                                            if (window.innerWidth >= 576) {
                                                setCompareView('compare');
                                                setCompareModal(true);
                                            } else {
                                                props.setStep('prebookCompare');
                                            }
                                        }}
                                        aria-label="Open comparison details"
                                    >
                                        Prebook vs Ride-Sharing
                                        <img src={img("arrow-long-down.svg")} alt=""/>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Exclusive Airport Pickup Perks — opens the "Why Choose us?" view
                            (desktop: 'more' view of the compare popup; mobile: dedicated step). */}
                        <div className="modalPages-spacing prebook-compare hopn-ride-perks-bar">
                            <ExclusivePerksBar onClick={() => {
                                if (window.innerWidth >= 576) {
                                    setCompareView('more');
                                    setCompareModal(true);
                                } else {
                                    props.setStep('whyChooseUs');
                                }
                            }}/>
                        </div>
                    </>
                )}
            </div>

            <ModalFooter>
                <div className="hopn-ride-footer-inner">
                    {!isLoading && (
                        /* Special request + coupon — toggle switches mirroring carSelection.
                           Live in the footer (scroll with it); laid out left/right to save height. */
                        <div className={`hopn-ride-actions${isLoggedIn ? '' : ' is-guest'}`}>
                            {/* Saved card — logged-in only; opens the shared Secure Payment
                                content. NOT a toggle like the two rows beside it: there is
                                nothing to switch on/off, so the whole row is the affordance. The
                                card that will be charged IS the label (no "Credit/Debit Card"
                                title above it), with the chevron right after it marking the row
                                as "opens a panel". */}
                            {isLoggedIn && (
                                <div className="hopn-ride-action hopn-ride-action--card"
                                     role="button" tabIndex={0} onClick={openPersonalCard}
                                     onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPersonalCard(); } }}>
                                    <span className="lbl">
                                        <img className="pc-icon" src={img("personal-icon.svg")} alt=""/>
                                        <span className="lbl-text">{defaultCardLabel || 'Add a card'}</span>
                                        <img className="chev" src={img("arrow-down.svg")} alt=""/>
                                    </span>
                                </div>
                            )}

                            {/* The two toggles share the other half of the row, stacked —
                                Spacial Request above Apply Coupon. */}
                            <div className="hopn-ride-actions-toggles">
                                <div className="hopn-ride-action">
                                    <span className="lbl" onClick={handleSpecialRequestClick}><div className="icon"><img src={img("dog-foot-print.svg")} alt=""/></div> Spacial Request</span>
                                    <input
                                        type="checkbox"
                                        id="scdl-special-request"
                                        className="hopn-checkbox"
                                        checked={isCarSpecialRequest}
                                        onChange={handleSpecialRequestClick}
                                    />
                                    <label htmlFor="scdl-special-request" className="hopn-checkbox-toggle"></label>
                                </div>

                                <div className="hopn-ride-action">
                                    <span className="lbl" onClick={() => window.innerWidth >= 576 ? setApplyCoupon(true) : props.setStep('applyCoupon')}><div className="icon"><img src={img("coupon.svg")} alt=""/></div> {hasCoupon ? 'Update Coupon' : 'Apply Coupon'}</span>
                                    <input
                                        type="checkbox"
                                        id="scdl-coupon"
                                        className="hopn-checkbox"
                                        checked={hasCoupon}
                                        onChange={() => window.innerWidth >= 576 ? setApplyCoupon(true) : props.setStep('applyCoupon')}
                                    />
                                    <label htmlFor="scdl-coupon" className="hopn-checkbox-toggle"></label>
                                </div>
                            </div>
                        </div>
                    )}
                    {recalculateError !== false && (<div className="error-message">{recalculateError}</div>)}
                    <button
                        className={`hopn-add-passenger-button hopn-ride-cta ${isEditMode ? "no-icon" : ""}`}
                        onClick={handleContinue}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader color="#fff"/>
                        ) : isEditMode ? "Done" : (
                            <>Confirm trip info & request</>
                        )}
                    </button>
                </div>
            </ModalFooter>
        </>
    );
}

// A perk row is either an inline accordion (onToggle + expandable body) or, when
// `onOpen` is passed, a button that opens a popup/step instead (chevron → right).
function PerkRow({icon, title, subtitle, open, onToggle, onOpen, children}) {
    const chevron = onOpen ? "arrow-down.svg" : (open ? "arrow-up.svg" : "arrow-down.svg");
    return (
        <div className={`hopn-ride-perk ${open ? "open" : ""}`}>
            <button type="button" onClick={onOpen || onToggle}>
                <span className="ic"><img src={img(icon)} alt=""/></span>
                <span className="txt">
                    <strong>{title} <img className="chev" src={img(chevron)} alt=""/></strong>
                    <small>{subtitle}</small>
                </span>
            </button>
            {!onOpen && open && <p>{children}</p>}
        </div>
    );
}

export default SelectedCarDetailsLatest;
